import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { getTranslation } from '../../../utils/translations';
import advancedBillingService from '../../../services/advancedBillingService';
import DiscountRuleModal from './DiscountRuleModal';

const DiscountRulesSection = ({ discountRules, countries, onRefresh, language }) => {
  const t = getTranslation(language);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRule, setSelectedRule] = useState(null);

  const getRuleTypeLabel = (type) => {
    const labels = {
      volume: t?.volumeDiscount,
      early_payment: t?.earlyPayment,
      loyalty: t?.loyalty,
      seasonal: t?.seasonal,
      custom: t?.custom
    };
    return labels?.[type] || type;
  };

  const getRuleTypeColor = (type) => {
    const colors = {
      volume: 'bg-blue-100 text-blue-700',
      early_payment: 'bg-green-100 text-green-700',
      loyalty: 'bg-purple-100 text-purple-700',
      seasonal: 'bg-orange-100 text-orange-700',
      custom: 'bg-gray-100 text-gray-700'
    };
    return colors?.[type] || 'bg-gray-100 text-gray-700';
  };

  const handleEdit = (rule) => {
    setSelectedRule(rule);
    setIsModalOpen(true);
  };

  const handleDelete = async (ruleId) => {
    if (window.confirm(language === 'FR' ? 'Êtes-vous sûr de vouloir supprimer cette règle ?' : 'Are you sure you want to delete this rule?')) {
      try {
        await advancedBillingService?.deleteDiscountRule(ruleId);
        onRefresh();
      } catch (error) {
        console.error('Error deleting rule:', error);
      }
    }
  };

  const handleToggleActive = async (rule) => {
    try {
      await advancedBillingService?.updateDiscountRule(rule?.id, {
        isActive: !rule?.isActive
      });
      onRefresh();
    } catch (error) {
      console.error('Error toggling rule status:', error);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-foreground">{t?.automaticDiscounts}</h2>
        <Button
          onClick={() => {
            setSelectedRule(null);
            setIsModalOpen(true);
          }}
          iconName="Plus"
          iconPosition="left"
        >
          {t?.createDiscountRule}
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {discountRules?.map((rule) => (
          <div
            key={rule?.id}
            className="bg-card border border-border rounded-lg p-4 hover:shadow-md transition-shadow duration-200"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <h3 className="font-semibold text-foreground mb-1">{rule?.ruleName}</h3>
                <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getRuleTypeColor(rule?.ruleType)}`}>
                  {getRuleTypeLabel(rule?.ruleType)}
                </span>
              </div>
              <button
                onClick={() => handleToggleActive(rule)}
                className={`p-1 rounded-full transition-colors ${
                  rule?.isActive ? 'bg-success/10 text-success' : 'bg-muted text-muted-foreground'
                }`}
              >
                <Icon name={rule?.isActive ? 'CheckCircle' : 'Circle'} size={20} />
              </button>
            </div>

            {rule?.description && (
              <p className="text-sm text-muted-foreground mb-3">{rule?.description}</p>
            )}

            <div className="space-y-2 mb-4">
              {rule?.discountPercentage && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">{t?.discountPercentage}:</span>
                  <span className="font-semibold text-success">{rule?.discountPercentage}%</span>
                </div>
              )}
              {rule?.minOrderQuantity && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">{t?.minOrderQuantity}:</span>
                  <span className="font-medium text-foreground">{rule?.minOrderQuantity}</span>
                </div>
              )}
              {rule?.earlyPaymentDays && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">{t?.earlyPaymentDays}:</span>
                  <span className="font-medium text-foreground">{rule?.earlyPaymentDays} {language === 'FR' ? 'jours' : 'days'}</span>
                </div>
              )}
              {rule?.country && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">{t?.countryName}:</span>
                  <span className="font-medium text-foreground">{rule?.country?.countryName}</span>
                </div>
              )}
              {rule?.countries && rule?.countries?.length > 0 && (
                <div className="flex items-start justify-between text-sm">
                  <span className="text-muted-foreground">{language === 'FR' ? 'Pays:' : 'Countries:'}:</span>
                  <div className="flex flex-wrap gap-1 justify-end max-w-[60%]">
                    {rule?.countries?.map((country, idx) => (
                      <span key={idx} className="inline-block px-2 py-0.5 bg-primary/10 text-primary rounded text-xs">
                        {country?.country_name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {(!rule?.country && (!rule?.countries || rule?.countries?.length === 0)) && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">{language === 'FR' ? 'Portée:' : 'Scope:'}:</span>
                  <span className="font-medium text-foreground">{language === 'FR' ? 'Tous les pays' : 'All countries'}</span>
                </div>
              )}
            </div>

            <div className="flex items-center gap-2 pt-3 border-t border-border">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleEdit(rule)}
                iconName="Edit"
                iconPosition="left"
                fullWidth
              >
                {t?.edit}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleDelete(rule?.id)}
                iconName="Trash2"
                iconPosition="left"
                fullWidth
              >
                {t?.delete}
              </Button>
            </div>
          </div>
        ))}
      </div>

      {discountRules?.length === 0 && (
        <div className="text-center py-12 bg-muted/30 rounded-lg border border-border">
          <Icon name="Tag" size={48} className="mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">{t?.noData}</p>
        </div>
      )}

      {isModalOpen && (
        <DiscountRuleModal
          rule={selectedRule}
          countries={countries}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedRule(null);
          }}
          onSave={() => {
            setIsModalOpen(false);
            setSelectedRule(null);
            onRefresh();
          }}
          language={language}
        />
      )}
    </div>
  );
};

export default DiscountRulesSection;